import LABTrainingPeaksSummary from './LABTrainingPeaksSummary';
// src/components/ActivityDetails/RunningActivityModal.js
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Row, Col } from 'react-bootstrap';
import ActivityActions from '../ActivityDetails/ActivityActions';
import ActivityPlannedCompletedTable from '../ActivityDetails/ActivityPlannedCompletedTable';
import ActivityMetricsTable from '../ActivityDetails/ActivityMetricsTable';
import ActivityHeader from '../ActivityDetails/ActivityHeader';
import ActivityDetailsForm from '../ActivityDetails/ActivityDetailsForm';
import FileUploadComponent from '../FileUploadComponent';
import ActivitySummary from '../ActivitySummary';
import moment from 'moment';
import { useTranslation } from '../../i18n';
import '../styles/ActivityDetailsModal.css';
import runningIcon from '../../assets/icons/running.png';
import cyclingIcon from '../../assets/icons/cycling.png';
import swimIcon from '../../assets/icons/swim.png';
import strengthIcon from '../../assets/icons/strength.png';
import WellnessForm from '../ActivityDetails/WellnessForm';

/* ---------------- Utils para detectar duración en estructuras anidadas ---------------- */
const pickDurationFromObj = (obj) => {
  let out = '';
  const scan = (o) => {
    if (!o || typeof o !== 'object' || out) return;
    for (const [k, v] of Object.entries(o)) {
      if (out) break;
      const key = String(k).toLowerCase();
      if (v == null) continue;
      if (typeof v === 'string') {
        const s = v.trim();
        if (!s) continue;
        if (s.startsWith('PT') || s.startsWith('pt')) { out = s; break; }
        if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(s)) { out = s; break; }
        if (/^\d+(?:\.\d+)?\s*(h|hr|hora|horas|min|minutos|s|seg|segundos)\b/i.test(s)) { out = s; break; }
        if (/^\d+(?:\.\d+)?$/.test(s) && /(dur|min|mins|minute|minutos|tiem|time)$/.test(key)) { out = s; break; }
      } else if (typeof v === 'number') {
        if (/(dur|min|mins|minute|minutos)$/.test(key)) { out = String(v); break; }
      } else if (typeof v === 'object') {
        scan(v);
      }
    }
  };
  scan(obj);
  return out;
};

/* ---------------- Helpers comunes (normalización) ---------------- */
const pad = (n) => String(n).padStart(2, '0');

const normalizeType = (raw = '') => {
  const t = String(raw).toLowerCase();
  if (t.includes('nad')) return 'nadar';
  if (t.includes('cicl') || t.includes('bike')) return 'ciclismo';
  if (t.includes('fuer') || t.includes('strength') || t.includes('gym')) return 'fuerza';
  if (t.includes('cam') || t.includes('walk')) return 'caminar';
  if (t.includes('desc') || t.includes('rest')) return 'descanso';
  if (t.includes('cor') || t.includes('run')) return 'correr';
  return 'otro';
};

const isZeroishDuration = (v) => {
  const s = String(v ?? '').trim();
  return (
    s === '' || s === '0' || s === '0.0' ||
    s === '00:00' || s === '0:00' ||
    s === '00:00:00' || s === '0:00:00' || s === '0:0:0'
  );
};

const toHHMMSS = (input, hintUnit = 'auto') => {
  if (input == null && input !== 0) return '';
  const s = String(input).trim();
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(s)) {
    const [h, m, sec = '00'] = s.split(':');
    const out = `${pad(h)}:${pad(m)}:${pad(sec)}`;
    return isZeroishDuration(out) ? '' : out;
  }
  const iso = /^P(T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)$/i.exec(s);
  if (iso) {
    const h = parseFloat(iso[2] || 0);
    const m = parseFloat(iso[3] || 0);
    const sec = parseFloat(iso[4] || 0);
    const total = Math.round(h * 3600 + m * 60 + sec);
    const hh = Math.floor(total / 3600), mm = Math.floor((total % 3600) / 60), ss = total % 60;
    const out = `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
    return isZeroishDuration(out) ? '' : out;
  }
  let total = 0, m;
  const re = /(\d+(?:\.\d+)?)\s*(h|hr|hora|horas|m|min|minutos|s|seg|segundos)/gi;
  while ((m = re.exec(s))) {
    const val = parseFloat(m[1]); const u = m[2].toLowerCase();
    if (['h','hr','hora','horas'].includes(u)) total += val * 3600;
    else if (['m','min','minutos'].includes(u)) total += val * 60;
    else total += val;
  }
  if (total > 0) {
    const hh = Math.floor(total / 3600), mm = Math.floor((total % 3600) / 60), ss = Math.floor(total % 60);
    const out = `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
    return isZeroishDuration(out) ? '' : out;
  }
  if (/^\d+(?:\.\d+)?$/.test(s)) {
    const num = parseFloat(s);
    const secs = hintUnit === 'minutes' ? Math.round(num * 60) : Math.round(num);
    const hh = Math.floor(secs / 3600), mm = Math.floor((secs % 3600) / 60), ss = secs % 60;
    const out = `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
    return isZeroishDuration(out) ? '' : out;
  }
  return '';
};

const toKmString = (input) => {
  if (input == null) return '';
  let s = String(input).trim().toLowerCase().replace(',', '.');
  if (/^\d+(\.(\d+))?\s*m$/.test(s)) return String(parseFloat(s) / 1000);
  if (/^\d+(\.(\d+))?\s*k$/.test(s)) return String(parseFloat(s));
  const num = parseFloat(s);
  return Number.isFinite(num) ? String(num) : '';
};

const durationToSeconds = (hhmmss = '') => {
  const [h = '0', m = '0', s = '0'] = String(hhmmss).split(':');
  return (+h) * 3600 + (+m) * 60 + (+s);
};

const computeEnd = (startIso, hhmmss) => {
  if (!startIso || !hhmmss) return startIso;
  const secs = durationToSeconds(hhmmss);
  return new Date(new Date(startIso).getTime() + secs * 1000).toISOString();
};

const resolveAssignedTo = (explicit) =>
  explicit || localStorage.getItem('selectedAthleteId') || localStorage.getItem('userId');

/* ---------------- State inicial ---------------- */
const initialActivityState = {
  title: '',
  description: '',
  preComments: '',
  postComments: '',
  planned: {
    duracion: '', distancia: '', ritmo_medio: '', calorias: '',
    desnivel_positivo: '', tte: '', tea: '', tss: ''
  },
  completed: {
    duracion: '', distancia: '', ritmo_medio: '', calorias: '',
    desnivel_positivo: '', tte: '', tea: '', tss: '',
    fileId: '', totalDescent: '', gap: '', vo2max: ''
  },
  metrics: {
    ritmo_medio: { min: '', avg: '', max: '' },
    ritmo_cardiaco: { min: '', avg: '', max: '' },
    cadencia: { min: '', avg: '', max: '' },
    velocidad: { min: '', avg: '', max: '' },
    gap: '', vo2max: ''
  }
};

const RunningActivityModal = ({
  show, onHide, activityType, onSave, onDelete,
  selectedDate, profilePicture, fetchEvents, athleteId: athleteIdProp
}) => {
  const { t } = useTranslation();
  const [profileData] = useState({ name: '', profilePicture: '' });
  const [runningThresholdPaceSec, setRunningThresholdPaceSec] = useState(null);

  useEffect(() => {
    const uid = athleteIdProp || localStorage.getItem('selectedAthleteId') || localStorage.getItem('userId');
    if (!uid) return;
    const base = (process.env.REACT_APP_API_URL || 'https://tzone-web-backend-1-2ev5.onrender.com');
    fetch(`${base}/api/athletes/training-zones/${uid}/VP`)
      .then(r => r.json())
      .then(d => { if (d?.threshold) setRunningThresholdPaceSec(Number(d.threshold)); })
      .catch(() => {});
  }, [athleteIdProp]);
  const [activity, setActivity] = useState(initialActivityState);
  const [ready, setReady] = useState(false);
  const [summaryApplied, setSummaryApplied] = useState(false);
  const lastActivityIdRef = useRef(null);

  const isExistingActivity = !!activityType?.id;

  const buildActivityFromType = (raw, untitledLabel) => {
    const completed = raw?.completed || {};
    const metrics = raw?.metrics || {};
    const srcPlanned = raw?.planned || raw?.extendedProps?.planned || {};

    let plannedDurHint = 'auto';
    let plannedDurRaw =
      srcPlanned?.duracion ?? srcPlanned?.duration ?? srcPlanned?.durationIso ??
      srcPlanned?.duracionMin ?? srcPlanned?.minutes ?? srcPlanned?.mins ??
      raw?.extendedProps?.duracion ?? raw?.extendedProps?.duration ??
      raw?.extendedProps?.durationIso ?? raw?.extendedProps?.duracionMin ??
      raw?.extendedProps?.minutes ?? raw?.extendedProps?.mins ??
      raw?.duration ?? raw?.durationIso ?? raw?.duracionMin ?? '';

    if (
      srcPlanned?.duracionMin != null || srcPlanned?.minutes != null || srcPlanned?.mins != null ||
      raw?.extendedProps?.duracionMin != null || raw?.extendedProps?.minutes != null || raw?.extendedProps?.mins != null
    ) {
      plannedDurHint = 'minutes';
    }

    if (!plannedDurRaw) {
      plannedDurRaw =
        pickDurationFromObj(srcPlanned) ||
        pickDurationFromObj(raw?.extendedProps) ||
        pickDurationFromObj(raw) || '';
    }

    const plannedDur = toHHMMSS(plannedDurRaw, plannedDurHint);
    const plannedDist = toKmString(
      srcPlanned?.distancia ?? srcPlanned?.distance ??
      raw?.extendedProps?.distancia ?? raw?.extendedProps?.distance ??
      raw?.distance ?? ''
    );

    /* eslint-disable no-console */
    console.log('🏷️ RunningActivityModal.buildActivityFromType', { plannedDurRaw, plannedDurShown: plannedDur, plannedDist });
    /* eslint-enable no-console */

    const planned = { ...(srcPlanned || {}), duracion: plannedDur, duration: plannedDur, distancia: plannedDist };

    return {
      ...initialActivityState,
      ...raw,
      title: raw?.title ?? untitledLabel,
      planned,
      completed: {
        duracion: isZeroishDuration(completed.duration || completed.duracion)
          ? '' : (completed.duration || completed.duracion || ''),
        distancia: completed.distance || completed.distancia || '',
        ritmo_medio: completed.avgPace || '',
        calorias: completed.calories || '',
        desnivel_positivo: completed.totalAscent || '',
        tss: completed.tss || '',
        tea: completed.tea || '',
        tte: completed.tte || '',
        totalDescent: completed.totalDescent || '',
        gap: completed.gap ?? '-',
        vo2max: completed.vo2max ?? '-',
        fileId: completed.fileId || ''
      },
      metrics
    };
  };

  useEffect(() => {
    const currId = activityType?.id ?? 'new';
    const lastId = lastActivityIdRef.current;
    const idChanged = currId !== lastId;
    if (show && (idChanged || !summaryApplied)) {
      setActivity(buildActivityFromType(activityType, t('activityLabels.running')));
      setReady(true);
      lastActivityIdRef.current = currId;
    }
  }, [activityType, show, summaryApplied]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!show) {
      setActivity(initialActivityState);
      setReady(false);
      setSummaryApplied(false);
      lastActivityIdRef.current = null;
    }
  }, [show]);

  const handleSave = () => {
    const userId = resolveAssignedTo(activity.assignedTo);
    const trainerId = localStorage.getItem('trainerId') || null;
    const { description, planned, title, type } = activity;


    const hasPlannedValue = Object.values(planned || {}).some(
      (val) => val && val.toString().trim() !== ''
    );
    if (!hasPlannedValue) {
      alert(t('activityDetails.validationPlannedRequired'));
      return;
    }
    if (!title || title.trim() === '') {
      alert(t('activityDetails.validationTitleRequired'));
      return;
    }
    if (!(type || activityType?.type)) {
      alert(t('activityDetails.validationTypeRequired'));
      return;
    }
    if (!userId) {
      alert(t('activityDetails.validationAthleteNotFound'));
      return;
    }

    const startIso = new Date(selectedDate || new Date()).toISOString();
    const dur = toHHMMSS(planned?.duracion || activity.duration || '');
    const dist = toKmString(planned?.distancia ?? planned?.distance ?? activity.distance ?? '');
    const endIso = dur ? computeEnd(startIso, dur) : startIso;

    const actividadFinal = {
      ...activity,
      assignedTo: userId,
      trainer: trainerId,
      type: normalizeType(type || activityType?.type || 'correr'),
      date: startIso,
      start: startIso,
      end: endIso,
      duration: dur || '',
      distance: dist || '',
      planned: { ...planned, duracion: dur || '', duration: dur || '', distancia: dist || '' },
      bgColor: activity.bgColor || '#37474F',
      external: !!activity.external
    };

    /* eslint-disable no-console */
    console.log('💾 RunningActivityModal.save payload', actividadFinal);
    /* eslint-enable no-console */

    if (typeof onSave === 'function') onSave(actividadFinal);
    onHide();
  };

  const handleDelete = () => {
    if (typeof onDelete === 'function' && activityType?.id) {
      onDelete(activityType.id);
      onHide();
    }
  };

  if (show && !ready) {
    return <div className="p-4">{t('activityDetails.loadingActivity')}</div>;
  }

  const hasFitFile = !!activity?.completed?.fileId;

  return (
    <Modal show={show} onHide={onHide} size="xl" dialogClassName="tz20-activity-dialog" contentClassName="tz20-activity-modal">
      <Modal.Header closeButton className="tz20-activity-header">
        <div className="tz20-activity-title-wrap">
          <span className="tz20-activity-icon" aria-hidden="true"><img src={runningIcon} alt="" /></span>
          <div>
            <Modal.Title className="tz20-activity-title">
              {t('activityDetails.activityModalTitle', { title: activity.title || t('activityLabels.running') })}
            </Modal.Title>
            <div className="tz20-activity-meta">
              {selectedDate ? moment(selectedDate).format('ddd D MMM YYYY') : moment().format('ddd D MMM YYYY')}
              {' · '}
              {activity?.completed?.fileId
                ? t('activityDetails.fitFileLoaded')
                : t('activityDetails.noFitFile')}
            </div>
          </div>
        </div>
        <div className="tz20-sport-tabs" aria-label={t('activityDetails.sportsTabsAriaLabel')}>
          <button type="button" className="active" aria-label={t('activityLabels.running')}><img src={runningIcon} alt="" /></button>
          <button type="button" aria-label={t('activityLabels.cycling')}><img src={cyclingIcon} alt="" /></button>
          <button type="button" aria-label={t('activityLabels.swimming')}><img src={swimIcon} alt="" /></button>
          <button type="button" aria-label={t('activityLabels.strength')}><img src={strengthIcon} alt="" /></button>
        </div>
      </Modal.Header>

      <Modal.Body className="tz20-activity-body">
        <Row className="tz20-activity-athlete-row">
          <ActivityHeader
            profilePicture={profilePicture}
            profileData={profileData}
            selectedDate={selectedDate}
          />
        </Row>

        <Row className="g-3 mb-3 tz20-activity-main">
          <Col xs={12} md={5}>
            <section className="tz20-activity-card tz20-activity-form-card">
              <ActivityDetailsForm
                title={activity.title}
                setTitle={(title) => setActivity((prev) => ({ ...prev, title }))}
                description={activity.description}
                setDescription={(description) => setActivity((prev) => ({ ...prev, description }))}
                preComments={activity.preComments}
                setPreComments={(preComments) => setActivity((prev) => ({ ...prev, preComments }))}
                postComments={activity.postComments}
                setPostComments={(postComments) => setActivity((prev) => ({ ...prev, postComments }))}
              />
              {isExistingActivity && (
                <div className="tz20-fit-upload">
                  <FileUploadComponent
                    activityId={activityType?.id || 'undefined'}
                    activityType="running"
                    onFileUpload={(data) => {
                      if (data.fileId) {
                        setActivity((prev) => ({
                          ...prev,
                          completed: { ...prev.completed, fileId: data.fileId }
                        }));
                        setSummaryApplied(true);
                        if (typeof fetchEvents === 'function') fetchEvents();
                      }
                    }}
                  />
                </div>
              )}
            </section>
          </Col>
          <Col xs={12} md={7}>
            <section className="tz20-activity-card tz20-plan-card">
              <div className="tz20-section-title tz20-section-title-accent">{t('running.plannedVsCompleted')}</div>
              <ActivityPlannedCompletedTable
                planned={activity.planned}
                completed={activity.completed}
                setPlanned={(p) => setActivity((a) => ({ ...a, planned: p }))}
                runningThresholdPaceSec={runningThresholdPaceSec}
              />
              <ActivityMetricsTable fileId={activity?.completed?.fileId} />
            </section>
          </Col>
          {hasFitFile && (
            <Col xs={12}>
              <section className="tz20-activity-card tz20-summary-card">
                <ActivitySummary
                fileId={activity?.completed?.fileId}
                activityId={activityType.id}
                date={activity?.date || activityType?.date}
                athleteId={athleteIdProp || activity?.assignedTo || activityType?.assignedTo || localStorage.getItem("selectedAthleteId") || localStorage.getItem("userId")}
                onSummaryData={({ completed, metrics }) => {
                  const ritmo =
                    (typeof completed.ritmo_medio === 'object'
                      ? (completed.ritmo_medio.avg || '')
                      : (completed.ritmo_medio || '')) ||
                    completed.avgPace || metrics?.ritmo_medio?.avg ||
                    metrics?.ritmo_medio || metrics?.avgPace || '';

                  const duracionRaw = completed.duration || completed.duracion || metrics?.duracion || '';
                  let duracion = duracionRaw;
                  if (typeof duracionRaw === 'number') {
                    duracion = moment.utc(moment.duration(duracionRaw, 'minutes').asMilliseconds()).format('HH:mm:ss');
                  }
                  if (isZeroishDuration(duracion)) duracion = '';

                  const distanciaVal = (() => {
                    const v = parseFloat(completed.distance ?? completed.distancia ?? metrics?.distance ?? metrics?.distancia ?? '');
                    return Number.isFinite(v) ? v : '';
                  })();
                  const caloriasVal = (() => {
                    const v = parseFloat(completed.calories ?? completed.kcal ?? completed.calorias ?? metrics?.calories ?? metrics?.kcal ?? metrics?.calorias ?? '');
                    return Number.isFinite(v) ? v : '';
                  })();
                  const desnivelVal = (() => {
                    const v = parseFloat(completed.desnivel_positivo ?? completed.elevation ?? completed.totalAscent ?? metrics?.desnivel_positivo ?? metrics?.elevation ?? '');
                    return Number.isFinite(v) ? v : '';
                  })();
                  const tssVal = (() => { const v = parseFloat(completed.tss ?? metrics?.tss ?? ''); return Number.isFinite(v) ? v : ''; })();
                  const teaVal = (() => { const v = parseFloat(completed.tea ?? metrics?.tea ?? ''); return Number.isFinite(v) ? v : ''; })();
                  const tteVal = (() => { const v = parseFloat(completed.tte ?? metrics?.tte ?? ''); return Number.isFinite(v) ? v : ''; })();

                  setActivity((prev) => ({
                    ...prev,
                    completed: {
                      ...prev.completed, ...completed,
                      ritmo_medio: ritmo,
                      distance: distanciaVal, distancia: distanciaVal,
                      duracion, duration: duracion,
                      calorias: caloriasVal, calories: caloriasVal,
                      desnivel_positivo: desnivelVal, totalAscent: desnivelVal, elevation: desnivelVal,
                      tss: tssVal, tea: teaVal, tte: tteVal,
                      fileId: prev.completed.fileId || completed.fileId || '',
                      gap: completed.gap ?? prev.completed.gap ?? '-',
                      vo2max: completed.vo2max ?? prev.completed.vo2max ?? '-',
                    },
                    metrics,
                  }));

                  setSummaryApplied(true);
                  if (typeof fetchEvents === 'function') fetchEvents();
                }}
              />
              </section>
            </Col>
          )}
        </Row>
        {!activity.completed?.fileId && (
          <WellnessForm
            athleteId={activity?.assignedTo || activityType?.assignedTo || localStorage.getItem("selectedAthleteId") || localStorage.getItem("userId")}
            activityId={activityType?.id}
            date={activity?.date || activityType?.date}
          />
        )}
        <LABTrainingPeaksSummary activityId={activityType?.id || activityType?._id} />
      </Modal.Body>
      <Modal.Footer className="tz20-activity-footer">
        <ActivityActions
          onSave={handleSave}
          onDelete={handleDelete}
          onHide={onHide}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default RunningActivityModal;
